import ApiClient from "../../methods/api/apiClient";

export const MODULE_CONFIG = {
  title: "Email Templates",
  subtitle:
    "Manage master transactional email templates, global fallback backups, and dynamic merge tags.",
  routeBase: "/email-templates",
  endpoints: {
    list: "email-templates/super-admin/list",
    create: "email-templates/super-admin/create",
    detail: "email-templates/super-admin/detail",
    update: "email-templates/super-admin/update",
    preview: "email-templates/preview",
    delete: "email-templates/super-admin/delete",
  },
};

// Exact 6 email types
export const STANDARD_EMAIL_TYPES = [
  { id: "QUOTE_NOTIFICATION", name: "Quote Notification", badgeClass: "bg-amber-50 text-amber-900 border-amber-200" },
  { id: "INQUIRY_REPLY", name: "Inquiry Reply", badgeClass: "bg-emerald-50 text-emerald-900 border-emerald-200" },
  { id: "TENANT_WELCOME", name: "Tenant Welcome", badgeClass: "bg-sky-50 text-sky-900 border-sky-200" },
  { id: "ORDER_STATUS_UPDATE", name: "Order Status Update", badgeClass: "bg-blue-50 text-blue-900 border-blue-200" },
  { id: "SUBSCRIPTION_RECEIPT", name: "Subscription Receipt", badgeClass: "bg-purple-50 text-purple-900 border-purple-200" },
  { id: "CUSTOM", name: "Custom Template", badgeClass: "bg-slate-100 text-slate-800 border-slate-300" },
];

export const EMAIL_TYPE_MAP = STANDARD_EMAIL_TYPES.reduce((acc, curr) => {
  acc[curr.id] = curr;
  return acc;
}, {});

// Dynamic Merge Variables Presets Categorized
export const VARIABLE_CATEGORIES = {
  branding: {
    label: "Platform & Organization Branding",
    variables: [
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_name", label: "Platform / Company Name", description: "Master organization name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", label: "Support Email", description: "Official support email address", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Official contact phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  recipient: {
    label: "Recipient & Customer",
    variables: [
      { key: "customer_name", label: "Customer Full Name", description: "Primary customer/recipient name", example: "Bob Johnson" },
      { key: "customer_email", label: "Customer Email Address", description: "Primary recipient email address", example: "bob.johnson@example.com" },
    ],
  },
  transaction: {
    label: "Transaction & Context",
    variables: [
      { key: "order_id", label: "Order ID", description: "Unique order reference number", example: "ORD-1092" },
      { key: "order_status", label: "Order Status", description: "Current order milestone status", example: "Dispatched" },
      { key: "quote_number", label: "Quote Number", description: "Unique formal estimate reference", example: "QT-7890" },
      { key: "quote_amount", label: "Quote Amount", description: "Total quoted estimate value", example: "$4,500.00" },
      { key: "inquiry_subject", label: "Inquiry Subject", description: "Subject of original customer inquiry", example: "Commercial Plumbing Inquiry" },
      { key: "inquiry_message", label: "Inquiry Reply Message", description: "Staff response to customer inquiry", example: "We have reviewed your inquiry and attached the survey." },
      { key: "plan_name", label: "Plan Name", description: "Active subscription plan tier", example: "Enterprise Unlimited" },
      { key: "amount_paid", label: "Amount Paid", description: "Total charged payment amount", example: "$199.00" },
      { key: "billing_period", label: "Billing Period", description: "Billing frequency or date range", example: "Monthly (Oct 2026 - Nov 2026)" },
      { key: "action_url", label: "Action Button URL", description: "Direct target link for call-to-action button", example: "https://portal.charlessmithassociates.com/orders/ORD-1092" },
    ],
  },
};

// Exact Variables Required Per Email Type
export const VARIABLES_BY_TYPE = {
  QUOTE_NOTIFICATION: {
    typeName: "Quote Notification",
    typeSpecific: [
      { key: "quote_number", label: "Quote Number", description: "Unique formal quote reference ID", example: "QT-2026-0042" },
      { key: "quote_amount", label: "Quote Amount", description: "Total estimated dollar amount", example: "$4,500.00" },
      { key: "quote_date", label: "Quote Date", description: "Date when quote was generated", example: "October 19, 2026" },
      { key: "expiry_date", label: "Expiration Date", description: "Quote valid until date", example: "November 18, 2026" },
      { key: "quote_status", label: "Quote Status", description: "Current status (Pending, Approved)", example: "Pending Review" },
      { key: "action_url", label: "Review Quote URL", description: "Direct link to review and accept quote", example: "https://portal.charlessmithassociates.com/quotes/QT-2026-0042" },
      { key: "items_summary", label: "Items Summary", description: "Overview of quoted services and parts", example: "Commercial Boiler Installation & Inspection" },
    ],
    recipient: [
      { key: "customer_name", label: "Customer Name", description: "Customer / Recipient full name", example: "Jane Miller" },
      { key: "customer_email", label: "Customer Email", description: "Recipient email address", example: "jane.miller@acmecorp.com" },
      { key: "customer_phone", label: "Customer Phone", description: "Customer contact phone number", example: "+1 (555) 019-2834" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Company / portal brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Support Email", description: "Official support email address", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Official contact phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  ORDER_STATUS_UPDATE: {
    typeName: "Order Status Update",
    typeSpecific: [
      { key: "order_id", label: "Order ID", description: "Unique order reference number", example: "ORD-98214" },
      { key: "order_status", label: "Order Status", description: "Current order milestone (e.g. Dispatched)", example: "Dispatched" },
      { key: "order_date", label: "Order Date", description: "Date order was placed", example: "October 18, 2026" },
      { key: "order_amount", label: "Order Total", description: "Total billed order amount", example: "$840.00" },
      { key: "tracking_number", label: "Tracking Number", description: "Courier shipment tracking number", example: "1Z9999999999999999" },
      { key: "carrier_name", label: "Carrier / Courier", description: "Shipping carrier name (FedEx, UPS)", example: "FedEx Express" },
      { key: "tracking_url", label: "Tracking URL", description: "Direct link to track shipment online", example: "https://www.fedex.com/fedextrack/?trknbr=1Z9999999999999999" },
      { key: "shipping_address", label: "Shipping Address", description: "Destination delivery address", example: "742 Evergreen Terrace, Springfield" },
      { key: "action_url", label: "View Order URL", description: "Portal order management link", example: "https://portal.charlessmithassociates.com/orders/ORD-98214" },
    ],
    recipient: [
      { key: "customer_name", label: "Customer Name", description: "Customer full name", example: "Bob Johnson" },
      { key: "customer_email", label: "Customer Email", description: "Customer email address", example: "bob.johnson@example.com" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Company / portal brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Support Email", description: "Official support email address", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Official contact phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  INQUIRY_REPLY: {
    typeName: "Inquiry Reply",
    typeSpecific: [
      { key: "inquiry_id", label: "Inquiry ID", description: "Customer support ticket reference ID", example: "INQ-4810" },
      { key: "inquiry_subject", label: "Inquiry Subject", description: "Original message subject line", example: "Commercial Plumbing Inquiry" },
      { key: "inquiry_message", label: "Customer Message", description: "Original text sent by customer", example: "Looking for quote on industrial water heater repair." },
      { key: "reply_message", label: "Staff Reply", description: "Official response message body", example: "Our specialist team has evaluated your request and scheduled a site inspection." },
      { key: "replied_by", label: "Replied By", description: "Name of support agent who replied", example: "Sarah Jenkins" },
      { key: "action_url", label: "Ticket Portal URL", description: "Direct link to ticket conversation", example: "https://portal.charlessmithassociates.com/inquiries/INQ-4810" },
    ],
    recipient: [
      { key: "customer_name", label: "Customer Name", description: "Inquirer full name", example: "Robert Smith" },
      { key: "customer_email", label: "Customer Email", description: "Inquirer email address", example: "robert.smith@gmail.com" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Company / portal brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Support Email", description: "Official support email address", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Official contact phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  TENANT_WELCOME: {
    typeName: "Tenant Welcome",
    typeSpecific: [
      { key: "tenant_name", label: "Tenant Organization", description: "Name of registered company", example: "Acme Industrial Logistics" },
      { key: "subdomain", label: "Tenant Subdomain", description: "Allocated portal subdomain", example: "acme" },
      { key: "login_url", label: "Portal Login URL", description: "Dedicated tenant login URL", example: "https://acme.charlessmithassociates.com/login" },
      { key: "admin_email", label: "Admin Email", description: "Primary administrator username", example: "admin@acmelogistics.com" },
      { key: "temporary_password", label: "Temporary Password", description: "One-time initial login password", example: "W#9kP!2mZ" },
      { key: "plan_name", label: "Subscribed Plan", description: "Assigned subscription plan tier", example: "Enterprise Pro" },
      { key: "activation_date", label: "Activation Date", description: "Organization onboarding date", example: "October 19, 2026" },
      { key: "action_url", label: "Launch Portal Link", description: "Direct link to tenant portal", example: "https://acme.charlessmithassociates.com" },
    ],
    recipient: [
      { key: "customer_name", label: "Admin Contact Name", description: "Primary contact full name", example: "Alice Cooper" },
      { key: "customer_email", label: "Admin Contact Email", description: "Primary contact email address", example: "alice@acmelogistics.com" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Master platform brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Support Email", description: "Master platform support email", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Master platform contact phone", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  SUBSCRIPTION_RECEIPT: {
    typeName: "Subscription Receipt",
    typeSpecific: [
      { key: "invoice_id", label: "Invoice Number", description: "Unique payment receipt ID", example: "INV-2026-108" },
      { key: "plan_name", label: "Plan Tier", description: "Subscribed tier name", example: "Enterprise Unlimited" },
      { key: "amount_paid", label: "Amount Paid", description: "Total charged payment amount", example: "$199.00" },
      { key: "billing_period", label: "Billing Period", description: "Billing frequency or date range", example: "Monthly (Oct 2026 - Nov 2026)" },
      { key: "payment_method", label: "Payment Method", description: "Card type and last 4 digits", example: "Visa ending in 4242" },
      { key: "transaction_date", label: "Transaction Date", description: "Date payment was processed", example: "October 19, 2026" },
      { key: "next_billing_date", label: "Next Billing Date", description: "Upcoming scheduled renewal date", example: "November 19, 2026" },
      { key: "action_url", label: "Download Receipt Link", description: "PDF invoice download URL", example: "https://portal.charlessmithassociates.com/billing/invoices/INV-2026-108" },
    ],
    recipient: [
      { key: "customer_name", label: "Subscriber Name", description: "Billing contact full name", example: "Michael Scott" },
      { key: "customer_email", label: "Billing Email", description: "Billing contact email address", example: "michael@dundermifflin.com" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Platform brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Billing Support Email", description: "Dedicated billing inquiries email", example: "billing@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Contact phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
  CUSTOM: {
    typeName: "Custom Template",
    typeSpecific: [
      { key: "custom_headline", label: "Custom Headline", description: "Main title or notice subject", example: "Important System Maintenance Notice" },
      { key: "custom_message", label: "Custom Message", description: "Main message body content", example: "Please review the updated service schedule for next week." },
      { key: "action_label", label: "Action Button Text", description: "Call-to-action button title", example: "View Details" },
      { key: "action_url", label: "Action URL", description: "Target link for the button", example: "https://portal.charlessmithassociates.com" },
    ],
    recipient: [
      { key: "customer_name", label: "Recipient Name", description: "Recipient full name", example: "Valued Customer" },
      { key: "customer_email", label: "Recipient Email", description: "Recipient email address", example: "user@example.com" },
    ],
    branding: [
      { key: "platform_name", label: "Platform Name", description: "Platform brand name", example: "Charles S. Smith & Associates" },
      { key: "platform_logo", label: "Platform Logo URL", description: "Direct image URL of platform logo", example: "/assets/img/logo.png" },
      { key: "platform_email", label: "Support Email", description: "Support email address", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", label: "Support Phone", description: "Support phone number", example: "+1 (555) 019-2834" },
      { key: "current_year", label: "Current Year", description: "Current four-digit year", example: "2026" },
    ],
  },
};

// Predefined Starter Email Content & Sample Values Per Type
export const PREDEFINED_TEMPLATES_BY_TYPE = {
  QUOTE_NOTIFICATION: {
    name: "Quote Estimate Notification",
    subject: "New Formal Estimate #{{quote_number}} Prepared for {{customer_name}}",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>Estimate Proposal #{{quote_number}}</h2>
  <p>Dear <strong>{{customer_name}}</strong>,</p>
  <p>We have prepared a formal estimate for your requested services. Please review the proposal details below:</p>
  <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 24px; margin: 20px 0;">
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Quote Reference:</strong> #{{quote_number}}</p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Total Estimated Amount:</strong> <span style="font-size: 18px; font-weight: 800; color: #115B9D;">{{quote_amount}}</span></p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Items Summary:</strong> {{items_summary}}</p>
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Valid Until:</strong> {{expiry_date}}</p>
  </div>
  <p>This estimate is valid for 30 calendar days. Feel free to review the itemized scopes and approve online.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">Review & Approve Quote →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      quote_number: "QT-2026-0042",
      quote_amount: "$4,500.00",
      quote_date: "October 19, 2026",
      expiry_date: "November 18, 2026",
      quote_status: "Pending Review",
      items_summary: "Commercial Boiler Installation & Safety Inspection",
      action_url: "https://portal.charlessmithassociates.com/quotes/QT-2026-0042",
      customer_name: "Jane Miller",
      customer_email: "jane.miller@acmecorp.com",
      customer_phone: "+1 (555) 019-2834",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "support@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
  ORDER_STATUS_UPDATE: {
    name: "Order Status Notification",
    subject: "Update on Order #{{order_id}} - Status: {{order_status}}",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>Order Status Update: #{{order_id}}</h2>
  <p>Dear <strong>{{customer_name}}</strong>,</p>
  <p>We are pleased to inform you that your order <strong>#{{order_id}}</strong> has been updated to <strong>{{order_status}}</strong>.</p>
  <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 24px; margin: 20px 0;">
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Order Number:</strong> #{{order_id}}</p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Current Status:</strong> <span style="font-size: 16px; font-weight: 700; color: #115B9D;">{{order_status}}</span></p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Tracking Number:</strong> {{tracking_number}} ({{carrier_name}})</p>
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Shipping Address:</strong> {{shipping_address}}</p>
  </div>
  <p>You can track the live progress and delivery milestones via your portal dashboard at any time.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">Track Order Progress →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      order_id: "ORD-98214",
      order_status: "Dispatched",
      order_date: "October 18, 2026",
      order_amount: "$840.00",
      tracking_number: "1Z9999999999999999",
      carrier_name: "FedEx Express",
      tracking_url: "https://www.fedex.com/fedextrack/?trknbr=1Z9999999999999999",
      shipping_address: "742 Evergreen Terrace, Springfield",
      action_url: "https://portal.charlessmithassociates.com/orders/ORD-98214",
      customer_name: "Bob Johnson",
      customer_email: "bob.johnson@example.com",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "support@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
  INQUIRY_REPLY: {
    name: "Customer Inquiry Reply",
    subject: "Response regarding your inquiry: {{inquiry_subject}}",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>Customer Inquiry Response</h2>
  <p>Hello <strong>{{customer_name}}</strong>,</p>
  <p>Thank you for contacting us regarding: <strong>"{{inquiry_subject}}"</strong> (Ticket #{{inquiry_id}}).</p>
  <div style="background-color: #f0f9ff; border-left: 4px solid #115B9D; padding: 18px 20px; border-radius: 0 8px 8px 0; margin: 20px 0;">
    <p style="margin: 0; font-size: 14px; color: #0c4a6e; font-weight: 700;">Representative Reply (by {{replied_by}}):</p>
    <p style="margin: 6px 0 0 0; font-size: 15px; color: #1e293b; line-height: 1.6;">{{reply_message}}</p>
  </div>
  <p>If you have any further questions, simply reply to this email or contact support at <strong>{{platform_email}}</strong>.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">View Ticket in Portal →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      inquiry_id: "INQ-4810",
      inquiry_subject: "Commercial Plumbing Inquiry",
      inquiry_message: "Looking for quote on industrial water heater repair.",
      reply_message: "Our specialist team has evaluated your request and scheduled a certified technician for site survey tomorrow morning.",
      replied_by: "Sarah Jenkins",
      action_url: "https://portal.charlessmithassociates.com/inquiries/INQ-4810",
      customer_name: "Robert Smith",
      customer_email: "robert.smith@gmail.com",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "support@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
  TENANT_WELCOME: {
    name: "Tenant Organization Welcome",
    subject: "Welcome to {{platform_name}} - Organization Portal Active",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>Welcome to {{platform_name}}!</h2>
  <p>Dear <strong>{{customer_name}}</strong>,</p>
  <p>Your tenant organization profile for <strong>{{tenant_name}}</strong> is now active and ready for use.</p>
  <div style="background-color: #f0f9ff; border: 1px solid #bae6fd; border-radius: 12px; padding: 18px 24px; margin: 20px 0;">
    <p style="margin: 0; font-size: 13px; color: #0369a1;"><strong>Portal URL:</strong> <a href="{{login_url}}" target="_blank" style="color: #115B9D; font-weight: 700;">{{login_url}}</a></p>
    <p style="margin: 6px 0; font-size: 13px; color: #0369a1;"><strong>Admin Username:</strong> {{admin_email}}</p>
    <p style="margin: 6px 0; font-size: 13px; color: #0369a1;"><strong>Initial Password:</strong> <code style="background-color: #e0f2fe; padding: 2px 6px; border-radius: 4px; font-weight: 700;">{{temporary_password}}</code></p>
    <p style="margin: 0; font-size: 13px; color: #0369a1;"><strong>Subscribed Plan:</strong> {{plan_name}}</p>
  </div>
  <p>Please log in and configure your team members, company branding, and integration settings.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">Launch Tenant Portal →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      tenant_name: "Acme Industrial Logistics",
      subdomain: "acme",
      login_url: "https://acme.charlessmithassociates.com/login",
      admin_email: "admin@acmelogistics.com",
      temporary_password: "W#9kP!2mZ",
      plan_name: "Enterprise Pro",
      activation_date: "October 19, 2026",
      action_url: "https://acme.charlessmithassociates.com",
      customer_name: "Alice Cooper",
      customer_email: "alice@acmelogistics.com",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "support@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
  SUBSCRIPTION_RECEIPT: {
    name: "Subscription Payment Receipt",
    subject: "Receipt for your {{platform_name}} subscription: {{amount_paid}}",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>Payment Receipt: {{invoice_id}}</h2>
  <p>Hello <strong>{{customer_name}}</strong>,</p>
  <p>Thank you for your business. We have successfully processed your scheduled subscription payment.</p>
  <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 24px; margin: 20px 0;">
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Invoice ID:</strong> {{invoice_id}}</p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Plan Tier:</strong> {{plan_name}} ({{billing_period}})</p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Amount Paid:</strong> <span style="font-size: 18px; font-weight: 800; color: #115B9D;">{{amount_paid}}</span></p>
    <p style="margin: 6px 0; font-size: 13px; color: #64748b;"><strong>Payment Method:</strong> {{payment_method}}</p>
    <p style="margin: 0; font-size: 13px; color: #64748b;"><strong>Next Billing Date:</strong> {{next_billing_date}}</p>
  </div>
  <p>A copy of your invoice is attached to this receipt and can be viewed anytime in your account.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">Download Official Invoice →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      invoice_id: "INV-2026-108",
      plan_name: "Enterprise Unlimited",
      amount_paid: "$199.00",
      billing_period: "Monthly (Oct 2026 - Nov 2026)",
      payment_method: "Visa ending in 4242",
      transaction_date: "October 19, 2026",
      next_billing_date: "November 19, 2026",
      action_url: "https://portal.charlessmithassociates.com/billing/invoices/INV-2026-108",
      customer_name: "Michael Scott",
      customer_email: "michael@dundermifflin.com",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "billing@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
  CUSTOM: {
    name: "Custom Platform Notification",
    subject: "Important Notice from {{platform_name}}",
    body_html: `<div style="background-color: #115B9D; padding: 24px 30px; border-radius: 12px 12px 0 0; text-align: left;">
  <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">{{platform_name}}</div>
  <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">Official System Notification</div>
</div>
<div style="padding: 28px 30px; background-color: #ffffff; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #334155; font-size: 15px; line-height: 1.6;">
  <h2>{{custom_headline}}</h2>
  <p>Dear <strong>{{customer_name}}</strong>,</p>
  <p>{{custom_message}}</p>
  <p>If you have any questions or require assistance, please contact our support team at <strong>{{platform_email}}</strong>.</p>
  <p style="text-align: center; margin-top: 25px;">
    <a href="{{action_url}}" target="_blank" style="background-color: #115B9D; color: #ffffff; padding: 12px 28px; text-decoration: none; font-weight: 700; border-radius: 8px; display: inline-block;">{{action_label}} →</a>
  </p>
</div>
<div style="background-color: #115B9D; padding: 22px 30px; text-align: center; border-radius: 0 0 12px 12px; color: #ffffff;">
  <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
    Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
  </p>
  <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
    &copy; {{current_year}} {{platform_name}}. All rights reserved.
  </p>
  <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
    This is an automated operational notification generated on behalf of your account.
  </p>
</div>`,
    sampleData: {
      custom_headline: "Scheduled System Maintenance Window",
      custom_message: "Our engineering team will perform scheduled maintenance on Sunday from 02:00 UTC to 04:00 UTC. The customer portal will experience brief intermittent downtime.",
      action_label: "View System Status",
      action_url: "https://status.charlessmithassociates.com",
      customer_name: "Valued Customer",
      customer_email: "client@example.com",
      platform_name: "Charles S. Smith & Associates",
      platform_logo: "/assets/img/logo.png",
      platform_email: "support@charlessmithassociates.com",
      platform_phone: "+1 (555) 019-2834",
      current_year: "2026",
    },
  },
};

// Helper: Wrap inner HTML into responsive email shell
export const wrapWithPlatformEmailShell = (innerHtml = "", emailType = "CUSTOM") => {
  if (innerHtml.includes("<!DOCTYPE") || innerHtml.includes("<html")) {
    return innerHtml;
  }

  const brandColor = "#115B9D";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Notification</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f1f5f9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #334155; font-size: 15px; line-height: 1.6;">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f1f5f9; padding: 32px 16px;">
    <tr>
      <td align="center">
        <!-- Master Email Card -->
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 620px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
          <!-- Header Banner (#115B9D) -->
          <tr>
            <td style="background-color: ${brandColor}; padding: 28px 32px; text-align: left;">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="left" style="vertical-align: middle;">
                    <div style="font-size: 20px; font-weight: 800; color: #ffffff; line-height: 1.25; margin: 0;">
                      {{platform_name}}
                    </div>
                    <div style="font-size: 12px; font-weight: 600; color: #bfdbfe; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px;">
                      Official System Notification
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Body Content -->
          <tr>
            <td style="padding: 32px; color: #334155; font-size: 15px; line-height: 1.6;">
              ${innerHtml}
            </td>
          </tr>
          <!-- Footer Banner (#115B9D) -->
          <tr>
            <td style="background-color: ${brandColor}; padding: 22px 32px; border-top: 1px solid rgba(255,255,255,0.15); text-align: center; color: #ffffff;">
              <p style="font-size: 13px; color: #ffffff; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600;">
                Support Contact: <strong style="color: #ffffff;">{{platform_email}}</strong> &bull; <span>{{platform_phone}}</span>
              </p>
              <p style="font-size: 12px; color: #bfdbfe; margin: 0; line-height: 1.5;">
                &copy; {{current_year}} {{platform_name}}. All rights reserved.
              </p>
              <p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0;">
                This is an automated operational notification generated on behalf of your account.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

export const COMMON_VARIABLE_PRESETS = [
  ...VARIABLE_CATEGORIES.branding.variables,
  ...VARIABLE_CATEGORIES.recipient.variables,
  ...VARIABLE_CATEGORIES.transaction.variables,
];

export const DEFAULT_BLOCKS = [
  {
    id: "blk-header-1",
    type: "header",
    data: {
      bg_color: "#115B9D",
      text_color: "#ffffff",
      subtitle: "Official System Notification",
      subtitle_color: "#bfdbfe",
      padding: "28px 32px",
      logo_show: true,
      logo_var: "{{platform_logo}}",
      logo_height: "38",
      logo_align: "left",
      name_show: true,
      name_var: "{{platform_name}}",
      name_font_size: "20",
      name_color: "#ffffff",
    },
  },
  {
    id: "blk-title-2",
    type: "heading",
    data: {
      text: "Order Status Update: #{{order_id}}",
      level: "h2",
      color: "#0f172a",
      fontSize: "22",
      align: "left",
    },
  },
  {
    id: "blk-body-3",
    type: "text",
    data: {
      html: "<p>Dear <strong>{{customer_name}}</strong>,</p><p>We are pleased to inform you that your order <strong>#{{order_id}}</strong> has been updated to <strong>{{order_status}}</strong>.</p><p>You can track the live status and shipping updates via your portal dashboard at any time.</p>",
      color: "#334155",
      fontSize: "15",
      align: "left",
    },
  },
  {
    id: "blk-btn-4",
    type: "button",
    data: {
      label: "Track Order Progress →",
      url: "{{action_url}}",
      bg_color: "#115B9D",
      text_color: "#ffffff",
      border_radius: "10",
      align: "center",
    },
  },
  {
    id: "blk-divider-5",
    type: "divider",
    data: {
      color: "#e2e8f0",
      thickness: "1",
    },
  },
  {
    id: "blk-footer-6",
    type: "footer",
    data: {
      bg_color: "#f8fafc",
      text_color: "#94a3b8",
      email_var: "{{platform_email}}",
      phone_var: "{{platform_phone}}",
      copyright_text: "© {{current_year}} {{platform_name}}. All rights reserved.",
      disclaimer: "This is an automated operational notification.",
      padding: "24px 32px",
    },
  },
];

// Default Visual Builder Configuration
export const DEFAULT_BUILDER_CONFIG = {
  blocks: DEFAULT_BLOCKS,
  header: {
    show: true,
    bg_color: "#115B9D",
    text_color: "#ffffff",
    subtitle: "Official System Notification",
    subtitle_color: "#bfdbfe",
    padding: "32px 36px",
    logo_show: true,
    logo_var: "{{platform_logo}}",
    logo_height: "42",
    logo_align: "left", // left | center | right
    name_show: true,
    name_var: "{{platform_name}}",
    name_font_size: "22",
    name_color: "#ffffff",
  },
  theme: {
    primary_color: "#115B9D",
    canvas_bg: "#f1f5f9",
    card_bg: "#ffffff",
    text_color: "#334155",
    font_family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    font_size: "15",
    border_radius: "16",
    card_max_width: "600",
  },
  content: {
    greeting: "Dear <strong>{{customer_name}}</strong>,",
    body_html: "<p>We are pleased to inform you that your order <strong>#{{order_id}}</strong> has been updated to <strong>{{order_status}}</strong>.</p><p>You can track the live status and shipping updates via your portal dashboard at any time.</p>",
  },
  cta: {
    show: true,
    label: "Track Order Progress →",
    url: "{{action_url}}",
    bg_color: "#115B9D",
    text_color: "#ffffff",
    border_radius: "10",
    align: "center", // left | center | right
  },
  footer: {
    show: true,
    bg_color: "#f8fafc",
    text_color: "#94a3b8",
    email_var: "{{platform_email}}",
    phone_var: "{{platform_phone}}",
    copyright_text: "© {{current_year}} {{platform_name}}. All rights reserved.",
    disclaimer: "This is an automated operational notification.",
  },
};

// Generates production-ready, bulletproof responsive email HTML
export const generateEmailHtml = (config) => {
  const theme = config?.theme || DEFAULT_BUILDER_CONFIG.theme;
  const canvasBg = theme.canvas_bg || "#f1f5f9";
  const cardBg = theme.card_bg || "#ffffff";
  const fontFamily = theme.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
  const fontSize = `${theme.font_size || "15"}px`;
  const textColor = theme.text_color || "#334155";
  const borderRadius = `${theme.border_radius || "16"}px`;
  const primaryColor = theme.primary_color || "#115B9D";

  // Check if block-based architecture is used
  if (Array.isArray(config?.blocks) && config.blocks.length > 0) {
    const renderedRows = config.blocks.map((block) => {
      const data = block.data || {};
      switch (block.type) {
        case "header": {
          const bg = data.bg_color || primaryColor;
          const isCentered = data.layout === "centered";
          const logoHeight = `${data.logo_height || "38"}px`;
          const nameFontSize = typeof data.name_font_size === "number" ? `${data.name_font_size}px` : (data.name_font_size || "20px");
          const nameColor = data.name_color || "#ffffff";
          const subtitleColor = data.subtitle_color || "#bfdbfe";
          const padding = data.padding_top !== undefined
            ? `${data.padding_top}px ${data.padding_right || 24}px ${data.padding_bottom || 20}px ${data.padding_left || 24}px`
            : (data.padding || "24px 28px");
          const fontFamily = data.fontFamily || "Arial, sans-serif";
          const fontWeight = data.isBold !== false ? "800" : "normal";
          const fontStyle = data.isItalic ? "italic" : "normal";

          if (isCentered) {
            return `
              <tr>
                <td style="background-color: ${bg}; padding: ${padding}; text-align: center;">
                  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
                    <tr>
                      <td align="center" style="text-align: center;">
                        ${
                          data.logo_show !== false && data.logo_var
                            ? `<div style="margin-bottom: ${data.name_show !== false ? "10px" : "0px"}; text-align: center;">
                                <img src="${data.logo_var}" alt="${data.name_var || "Logo"}" height="${logoHeight}" style="height: ${logoHeight}; max-height: ${logoHeight}; width: auto; object-fit: contain; display: inline-block; border: 0;" />
                              </div>`
                            : ""
                        }
                        ${
                          data.name_show !== false
                            ? `<div style="font-size: ${nameFontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; color: ${nameColor}; line-height: 1.25; margin: 0; text-align: center; font-family: ${fontFamily};">
                                ${data.name_var || "{{platform_name}}"}
                              </div>`
                            : ""
                        }
                        ${
                          data.subtitle
                            ? `<div style="font-size: 12px; font-weight: 600; color: ${subtitleColor}; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 6px; text-align: center; font-family: ${fontFamily};">
                                ${data.subtitle}
                              </div>`
                            : ""
                        }
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            `;
          }

          // Side-by-Side layout: Logo on left, Company Name & Subtitle on right
          return `
            <tr>
              <td style="background-color: ${bg}; padding: ${padding};">
                <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
                  <tr>
                    <td align="left" style="vertical-align: middle; text-align: left;">
                      ${
                        data.logo_show !== false && data.logo_var
                          ? `<img src="${data.logo_var}" alt="${data.name_var || "Logo"}" height="${logoHeight}" style="height: ${logoHeight}; max-height: ${logoHeight}; width: auto; object-fit: contain; display: block; border: 0;" />`
                          : ""
                      }
                    </td>
                    <td align="right" style="vertical-align: middle; text-align: right;">
                      ${
                        data.name_show !== false
                          ? `<div style="font-size: ${nameFontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; color: ${nameColor}; line-height: 1.25; margin: 0; text-align: right; font-family: ${fontFamily};">
                              ${data.name_var || "{{platform_name}}"}
                            </div>`
                          : ""
                      }
                      ${
                        data.subtitle
                          ? `<div style="font-size: 12px; font-weight: 600; color: ${subtitleColor}; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; text-align: right; font-family: ${fontFamily};">
                              ${data.subtitle}
                            </div>`
                          : ""
                      }
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          `;
        }
        case "heading": {
          const padding = data.padding_top !== undefined
            ? `${data.padding_top}px ${data.padding_right || 24}px ${data.padding_bottom || 8}px ${data.padding_left || 24}px`
            : "18px 32px 8px 32px";
          const fontFamily = data.fontFamily || "Arial, sans-serif";
          const fontWeight = data.isBold !== false ? "800" : "normal";
          const fontStyle = data.isItalic ? "italic" : "normal";
          const fontSize = typeof data.fontSize === "number" ? `${data.fontSize}px` : (data.fontSize || "22px");
          const align = data.align || "center";
          return `
            <tr>
              <td style="padding: ${padding}; text-align: ${align};">
                <h2 style="margin: 0; color: ${data.color || "#0f172a"}; font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; font-family: ${fontFamily}; line-height: 1.3; text-align: ${align};">
                  ${data.text || "Title Text"}
                </h2>
              </td>
            </tr>
          `;
        }
        case "text": {
          const padding = data.padding_top !== undefined
            ? `${data.padding_top}px ${data.padding_right || 24}px ${data.padding_bottom || 12}px ${data.padding_left || 24}px`
            : "10px 32px";
          const fontFamily = data.fontFamily || "Arial, sans-serif";
          const fontSize = typeof data.fontSize === "number" ? `${data.fontSize}px` : (data.fontSize || "15px");
          const align = data.align || "left";
          return `
            <tr>
              <td style="padding: ${padding}; text-align: ${align}; color: ${data.color || textColor}; font-size: ${fontSize}; font-family: ${fontFamily}; line-height: 1.6;">
                ${data.html || data.text || "<p>Standard body text...</p>"}
              </td>
            </tr>
          `;
        }
        case "button": {
          const btnBg = data.bg_color || primaryColor;
          const btnTextColor = data.text_color || "#ffffff";
          const btnRadius = `${data.border_radius || "8"}px`;
          const btnAlign = data.align || "center";
          const padding = data.padding_top !== undefined
            ? `${data.padding_top}px ${data.padding_right || 24}px ${data.padding_bottom || 16}px ${data.padding_left || 24}px`
            : "16px 32px";
          const fontSize = typeof data.fontSize === "number" ? `${data.fontSize}px` : (data.fontSize || "14px");
          const fontFamily = data.fontFamily || "Arial, sans-serif";
          const fontWeight = data.isBold !== false ? "700" : "normal";

          return `
            <tr>
              <td style="padding: ${padding}; text-align: ${btnAlign};" align="${btnAlign}">
                <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="margin: 0 ${btnAlign === "left" ? "auto 0 0" : btnAlign === "right" ? "0 0 auto auto" : "auto"};">
                  <tr>
                    <td align="${btnAlign}" style="border-radius: ${btnRadius}; background-color: ${btnBg};">
                      <a href="${data.url || "{{action_url}}"}" target="_blank" style="display: inline-block; padding: 13px 32px; font-size: ${fontSize}; font-family: ${fontFamily}; font-weight: ${fontWeight}; color: ${btnTextColor}; text-decoration: none; border-radius: ${btnRadius}; letter-spacing: 0.3px;">
                        ${data.label || "Click Here →"}
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          `;
        }
        case "image": {
          const imgAlign = data.align || "center";
          const imgHeight = data.height ? `height="${data.height}"` : "";
          const imgWidth = data.width ? `width="${data.width}"` : "";
          const imgElement = `<img src="${data.url || "{{platform_logo}}"}" alt="${data.alt || "Image"}" ${imgWidth} ${imgHeight} style="max-width: 100%; height: auto; border-radius: 8px; display: inline-block; border: 0;" />`;

          return `
            <tr>
              <td style="padding: 16px 32px; text-align: ${imgAlign};">
                ${data.link_url ? `<a href="${data.link_url}" target="_blank">${imgElement}</a>` : imgElement}
              </td>
            </tr>
          `;
        }
        case "divider": {
          return `
            <tr>
              <td style="padding: 16px 32px;">
                <hr style="border: none; border-top: ${data.thickness || 1}px solid ${data.color || "#e2e8f0"}; margin: 0;" />
              </td>
            </tr>
          `;
        }
        case "footer": {
          const ftrBg = data.bg_color || primaryColor;
          const ftrTextColor = data.text_color || "#ffffff";
          const padding = data.padding_top !== undefined
            ? `${data.padding_top}px ${data.padding_right || 24}px ${data.padding_bottom || 20}px ${data.padding_left || 24}px`
            : (data.padding || "22px 32px");
          const fontFamily = data.fontFamily || "Arial, sans-serif";
          const fontSize = typeof data.fontSize === "number" ? `${data.fontSize}px` : (data.fontSize || "12px");
          const align = data.align || "center";

          return `
            <tr>
              <td style="background-color: ${ftrBg}; padding: ${padding}; border-top: 1px solid rgba(255,255,255,0.15); text-align: ${align}; color: ${ftrTextColor}; font-family: ${fontFamily};">
                ${
                  data.email_var || data.phone_var
                    ? `<p style="font-size: 13px; color: ${ftrTextColor}; margin: 0 0 6px 0; line-height: 1.5; font-weight: 600; text-align: ${align};">
                        Support Contact: <strong style="color: ${ftrTextColor};">${data.email_var || "{{platform_email}}"}</strong>
                        ${data.phone_var ? ` &bull; <span>${data.phone_var}</span>` : ""}
                      </p>`
                    : ""
                }
                <p style="font-size: ${fontSize}; color: ${data.secondary_color || "#bfdbfe"}; margin: 0; line-height: 1.5; text-align: ${align};">
                  ${data.copyright_text || "© {{current_year}} {{platform_name}}. All rights reserved."}
                </p>
                ${
                  data.disclaimer
                    ? `<p style="font-size: 11px; color: #93c5fd; margin: 6px 0 0 0; text-align: ${align};">
                        ${data.disclaimer}
                      </p>`
                    : ""
                }
              </td>
            </tr>
          `;
        }
        default:
          return "";
      }
    }).join("");

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Notification</title>
</head>
<body style="margin: 0; padding: 0; background-color: ${canvasBg}; font-family: ${fontFamily}; color: ${textColor}; font-size: ${fontSize};">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: ${canvasBg}; padding: 32px 16px;">
    <tr>
      <td align="center">
        <!-- Master Card -->
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: ${theme.card_max_width || "600"}px; background-color: ${cardBg}; border-radius: ${borderRadius}; overflow: hidden; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
          ${renderedRows}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  }

  // Fallback to legacy config structure if blocks array is not present
  const header = config?.header || DEFAULT_BUILDER_CONFIG.header;
  const content = config?.content || DEFAULT_BUILDER_CONFIG.content;
  const cta = config?.cta || DEFAULT_BUILDER_CONFIG.cta;
  const footer = config?.footer || DEFAULT_BUILDER_CONFIG.footer;

  // Header section
  let headerHtml = "";
  if (header.show) {
    const headerBg = header.bg_color || primaryColor;
    const headerAlign = header.logo_align || "left";
    const logoHeight = `${header.logo_height || "42"}px`;
    const nameFontSize = `${header.name_font_size || "22"}px`;
    const nameColor = header.name_color || "#ffffff";

    headerHtml = `
      <tr>
        <td style="background-color: ${headerBg}; padding: ${header.padding || "32px 36px"}; text-align: ${headerAlign};">
          ${
            header.logo_show && header.logo_var
              ? `<div style="margin-bottom: ${header.name_show ? "12px" : "0px"}; text-align: ${headerAlign};">
                  <img src="${header.logo_var}" alt="${header.name_var || "Logo"}" height="${logoHeight}" style="height: ${logoHeight}; max-height: ${logoHeight}; width: auto; object-fit: contain; display: inline-block; border: 0;" />
                </div>`
              : ""
          }
          ${
            header.name_show
              ? `<div style="font-size: ${nameFontSize}; font-weight: 800; color: ${nameColor}; line-height: 1.25; margin: 0;">
                  ${header.name_var || "{{platform_name}}"}
                </div>`
              : ""
          }
          ${
            header.subtitle
              ? `<div style="font-size: 13px; font-weight: 600; color: ${header.subtitle_color || "#bfdbfe"}; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 6px;">
                  ${header.subtitle}
                </div>`
              : ""
          }
        </td>
      </tr>
    `;
  }

  // CTA Button
  let ctaHtml = "";
  if (cta.show && cta.label && cta.url) {
    const ctaBg = cta.bg_color || primaryColor;
    const ctaTextColor = cta.text_color || "#ffffff";
    const ctaRadius = `${cta.border_radius || "10"}px`;
    const ctaAlign = cta.align || "center";

    ctaHtml = `
      <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="margin: 28px ${ctaAlign === "left" ? "0" : ctaAlign === "right" ? "0 0 0 auto" : "auto"};">
        <tr>
          <td align="${ctaAlign}" style="border-radius: ${ctaRadius}; background-color: ${ctaBg};">
            <a href="${cta.url}" target="_blank" style="display: inline-block; padding: 14px 34px; font-size: 14px; font-weight: 700; color: ${ctaTextColor}; text-decoration: none; border-radius: ${ctaRadius}; letter-spacing: 0.3px;">
              ${cta.label}
            </a>
          </td>
        </tr>
      </table>
    `;
  }

  // Footer section
  let footerHtml = "";
  if (footer.show) {
    const footerBg = footer.bg_color || "#f8fafc";
    const footerTextColor = footer.text_color || "#94a3b8";

    footerHtml = `
      <tr>
        <td style="background-color: ${footerBg}; padding: 24px 36px; border-top: 1px solid #e2e8f0; text-align: center;">
          ${
            footer.email_var || footer.phone_var
              ? `<p style="font-size: 12px; color: #64748b; margin: 0 0 8px 0; line-height: 1.5;">
                  Support: <strong style="color: ${primaryColor};">${footer.email_var || "{{platform_email}}"}</strong>
                  ${footer.phone_var ? ` &bull; <strong>${footer.phone_var}</strong>` : ""}
                </p>`
              : ""
          }
          <p style="font-size: 12px; color: ${footerTextColor}; margin: 0; line-height: 1.5;">
            ${footer.copyright_text || "© {{current_year}} {{platform_name}}. All rights reserved."}
          </p>
          ${
            footer.disclaimer
              ? `<p style="font-size: 11px; color: #cbd5e1; margin: 6px 0 0 0;">
                  ${footer.disclaimer}
                </p>`
              : ""
          }
        </td>
      </tr>
    `;
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Notification</title>
</head>
<body style="margin: 0; padding: 0; background-color: ${canvasBg}; font-family: ${fontFamily}; color: ${textColor}; font-size: ${fontSize};">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: ${canvasBg}; padding: 32px 16px;">
    <tr>
      <td align="center">
        <!-- Master Card -->
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: ${theme.card_max_width || "600"}px; background-color: ${cardBg}; border-radius: ${borderRadius}; overflow: hidden; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
          ${headerHtml}
          <!-- Body Content Area -->
          <tr>
            <td style="padding: 36px 36px 28px 36px; color: ${textColor}; font-size: ${fontSize}; line-height: 1.6;">
              ${content.greeting ? `<p style="margin-top: 0; margin-bottom: 20px; font-size: 16px; font-weight: 600;">${content.greeting}</p>` : ""}
              ${content.body_html || ""}
              ${ctaHtml}
            </td>
          </tr>
          ${footerHtml}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// Pre-built Starter Templates with Full Builder Configurations
export const STARTER_TEMPLATES = [
  {
    id: "starter_order_status",
    name: "Master Order Status Notification",
    email_type: "ORDER_STATUS_UPDATE",
    subject: "Update on Order #{{order_id}} - {{order_status}}",
    description: "Responsive card with logo header, status pill, order reference, tracking button, and signature.",
    builder_config: {
      ...DEFAULT_BUILDER_CONFIG,
      header: {
        ...DEFAULT_BUILDER_CONFIG.header,
        bg_color: "#115B9D",
        subtitle: "Order Status Update",
      },
      content: {
        greeting: "Dear <strong>{{customer_name}}</strong>,",
        body_html: `<p>We are pleased to inform you that the status of your order <strong>#{{order_id}}</strong> has been updated to <strong>{{order_status}}</strong>.</p>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 24px; margin: 24px 0;">
          <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">Order Number</div>
          <div style="font-size: 18px; font-weight: 800; color: #0f172a; margin-top: 4px;">#{{order_id}}</div>
        </div>
        <p>You can follow the shipment or review all order milestones directly inside your client portal.</p>`,
      },
      cta: {
        show: true,
        label: "Track Order Progress →",
        url: "{{action_url}}",
        bg_color: "#115B9D",
        text_color: "#ffffff",
        border_radius: "10",
        align: "center",
      },
    },
    variables: [
      { key: "platform_logo", description: "Platform Logo URL", example: "/assets/img/logo.png" },
      { key: "platform_name", description: "Platform Name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", description: "Platform Support Email", example: "support@charlessmithassociates.com" },
      { key: "customer_name", description: "Customer Name", example: "Bob Johnson" },
      { key: "order_id", description: "Order ID", example: "ORD-1092" },
      { key: "order_status", description: "Order Status", example: "Dispatched" },
      { key: "action_url", description: "Track Order Link", example: "https://portal.charlessmithassociates.com/orders/ORD-1092" },
    ],
    body_text: "Dear {{customer_name}},\n\nYour order {{order_id}} status has been updated to {{order_status}}.\n\nTrack your order here: {{action_url}}\n\nThank you for choosing {{platform_name}}.",
  },
  {
    id: "starter_quote_notification",
    name: "Quote Estimate Notification",
    email_type: "QUOTE_NOTIFICATION",
    subject: "New Quote #{{quote_number}} Prepared for {{customer_name}}",
    description: "Formal estimate presentation with quote number, total amount, and review CTA.",
    builder_config: {
      ...DEFAULT_BUILDER_CONFIG,
      header: {
        ...DEFAULT_BUILDER_CONFIG.header,
        bg_color: "#d97706",
        subtitle: "Official Estimate Proposal",
      },
      theme: {
        ...DEFAULT_BUILDER_CONFIG.theme,
        primary_color: "#d97706",
      },
      content: {
        greeting: "Dear <strong>{{customer_name}}</strong>,",
        body_html: `<p>We have prepared a formal estimate for your requested services. Please review the proposal details below:</p>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px 24px; margin: 24px 0; display: flex; justify-content: space-between;">
          <div>
            <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">Quote Number</div>
            <div style="font-size: 18px; font-weight: 800; color: #0f172a; margin-top: 4px;">#{{quote_number}}</div>
          </div>
          <div>
            <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">Estimated Total</div>
            <div style="font-size: 20px; font-weight: 900; color: #d97706; margin-top: 4px;">{{quote_amount}}</div>
          </div>
        </div>
        <p>This estimate is valid for 30 calendar days. Feel free to review the itemized scopes and approve online.</p>`,
      },
      cta: {
        show: true,
        label: "Review & Approve Quote →",
        url: "{{action_url}}",
        bg_color: "#d97706",
        text_color: "#ffffff",
        border_radius: "10",
        align: "center",
      },
    },
    variables: [
      { key: "platform_logo", description: "Platform Logo URL", example: "/assets/img/logo.png" },
      { key: "platform_name", description: "Platform Name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", description: "Platform Support Email", example: "support@charlessmithassociates.com" },
      { key: "customer_name", description: "Customer Name", example: "Jane Miller" },
      { key: "quote_number", description: "Quote Number", example: "QT-7890" },
      { key: "quote_amount", description: "Total Quote Value", example: "$4,500.00" },
      { key: "action_url", description: "Review Quote URL", example: "https://portal.charlessmithassociates.com/quotes/QT-7890" },
    ],
    body_text: "Dear {{customer_name}},\n\nA new formal quote #{{quote_number}} has been generated for you by {{platform_name}} in the amount of {{quote_amount}}.\n\nReview and approve here: {{action_url}}",
  },
  {
    id: "starter_inquiry_reply",
    name: "Customer Inquiry Reply",
    email_type: "INQUIRY_REPLY",
    subject: "Response regarding your inquiry: {{inquiry_subject}}",
    description: "Support response template featuring inquiry subject and agent reply text.",
    builder_config: {
      ...DEFAULT_BUILDER_CONFIG,
      header: {
        ...DEFAULT_BUILDER_CONFIG.header,
        bg_color: "#059669",
        subtitle: "Client Inquiries Support",
      },
      theme: {
        ...DEFAULT_BUILDER_CONFIG.theme,
        primary_color: "#059669",
      },
      content: {
        greeting: "Hello <strong>{{customer_name}}</strong>,",
        body_html: `<p style="font-size: 14px; color: #64748b; margin-bottom: 20px;">
          Regarding your inquiry: <strong style="color: #0f172a;">"{{inquiry_subject}}"</strong>
        </p>
        <div style="background-color: #f8fafc; border-left: 4px solid #059669; padding: 20px; border-radius: 0 8px 8px 0; margin-bottom: 28px;">
          <p style="margin: 0; font-size: 15px; color: #1e293b; line-height: 1.6; white-space: pre-line;">
            {{inquiry_message}}
          </p>
        </div>
        <p style="font-size: 13px; line-height: 1.5; color: #64748b;">
          If you have additional questions, reply directly to this email or contact us at <strong>{{platform_phone}}</strong>.
        </p>`,
      },
      cta: {
        show: false,
        label: "",
        url: "",
      },
    },
    variables: [
      { key: "platform_logo", description: "Platform Logo URL", example: "/assets/img/logo.png" },
      { key: "platform_name", description: "Platform Name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", description: "Support Email", example: "support@charlessmithassociates.com" },
      { key: "platform_phone", description: "Phone Number", example: "+1 (555) 019-2834" },
      { key: "customer_name", description: "Customer Name", example: "Robert Smith" },
      { key: "inquiry_subject", description: "Subject of Inquiry", example: "Commercial Plumbing Inquiry" },
      { key: "inquiry_message", description: "Response Content", example: "Thank you for reaching out. A specialist has been assigned to your case." },
    ],
    body_text: "Hello {{customer_name}},\n\nThank you for contacting {{platform_name}} regarding '{{inquiry_subject}}'.\n\n{{inquiry_message}}\n\nWarm regards,\n{{platform_name}} Support Team",
  },
  {
    id: "starter_tenant_welcome",
    name: "Tenant Organization Welcome",
    email_type: "TENANT_WELCOME",
    subject: "Welcome to {{platform_name}}, {{customer_name}}!",
    description: "Multi-tenant onboarding email with login URL and setup instructions.",
    builder_config: {
      ...DEFAULT_BUILDER_CONFIG,
      header: {
        ...DEFAULT_BUILDER_CONFIG.header,
        bg_color: "#0284c7",
        subtitle: "Organization Activation",
      },
      theme: {
        ...DEFAULT_BUILDER_CONFIG.theme,
        primary_color: "#0284c7",
      },
      content: {
        greeting: "Welcome <strong>{{customer_name}}</strong>!",
        body_html: `<p>Your tenant administrative portal for <strong>{{platform_name}}</strong> has been configured.</p>
        <p>You can now manage your subscription plans, invite sub-administrators, dispatch quotes, and view your operations analytics.</p>`,
      },
      cta: {
        show: true,
        label: "Launch Tenant Portal →",
        url: "{{action_url}}",
        bg_color: "#0284c7",
        text_color: "#ffffff",
        border_radius: "10",
        align: "center",
      },
    },
    variables: [
      { key: "platform_logo", description: "Platform Logo URL", example: "/assets/img/logo.png" },
      { key: "platform_name", description: "Platform Name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", description: "Support Email", example: "support@charlessmithassociates.com" },
      { key: "customer_name", description: "Admin Name", example: "Alice Cooper" },
      { key: "action_url", description: "Portal URL", example: "https://portal.charlessmithassociates.com" },
    ],
    body_text: "Welcome {{customer_name}}!\n\nYour organization profile with {{platform_name}} is now ready.\n\nAccess your dashboard: {{action_url}}",
  },
  {
    id: "starter_subscription_receipt",
    name: "Subscription Payment Receipt",
    email_type: "SUBSCRIPTION_RECEIPT",
    subject: "Receipt for your {{platform_name}} subscription - {{amount_paid}}",
    description: "Clean invoice receipt confirming plan renewal and billing period.",
    builder_config: {
      ...DEFAULT_BUILDER_CONFIG,
      header: {
        ...DEFAULT_BUILDER_CONFIG.header,
        bg_color: "#7c3aed",
        subtitle: "Subscription Billing",
      },
      theme: {
        ...DEFAULT_BUILDER_CONFIG.theme,
        primary_color: "#7c3aed",
      },
      content: {
        greeting: "Hello <strong>{{customer_name}}</strong>,",
        body_html: `<p>Thank you for your business. Your recent subscription invoice has been processed successfully:</p>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px 24px; margin: 24px 0;">
          <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 12px;">
            <span style="color: #64748b; font-size: 13px;">Plan Tier</span>
            <strong style="color: #0f172a; font-size: 14px;">{{plan_name}}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 12px;">
            <span style="color: #64748b; font-size: 13px;">Billing Interval</span>
            <span style="color: #0f172a; font-size: 14px; font-weight: 600;">{{billing_period}}</span>
          </div>
          <div style="display: flex; justify-content: space-between; padding-top: 4px;">
            <span style="color: #0f172a; font-size: 14px; font-weight: 800;">Total Paid</span>
            <strong style="color: #7c3aed; font-size: 20px; font-weight: 900;">{{amount_paid}}</strong>
          </div>
        </div>`,
      },
      cta: {
        show: true,
        label: "View Invoices & Billing Details →",
        url: "{{action_url}}",
        bg_color: "#7c3aed",
        text_color: "#ffffff",
        border_radius: "10",
        align: "center",
      },
    },
    variables: [
      { key: "platform_logo", description: "Platform Logo URL", example: "/assets/img/logo.png" },
      { key: "platform_name", description: "Platform Name", example: "Charles S. Smith & Associates" },
      { key: "platform_email", description: "Support Email", example: "billing@charlessmithassociates.com" },
      { key: "customer_name", description: "Subscriber Name", example: "Michael Scott" },
      { key: "plan_name", description: "Plan Name", example: "Enterprise Unlimited" },
      { key: "amount_paid", description: "Amount Paid", example: "$199.00" },
      { key: "billing_period", description: "Billing Interval", example: "Monthly (Oct 2026 - Nov 2026)" },
      { key: "action_url", description: "Billing Portal Link", example: "https://portal.charlessmithassociates.com/billing" },
    ],
    body_text: "Thank you for your payment, {{customer_name}}.\n\nPlan: {{plan_name}}\nAmount: {{amount_paid}}\nPeriod: {{billing_period}}\n\nView receipt: {{action_url}}",
  },
];

// Helper: resolve starter template HTML
STARTER_TEMPLATES.forEach((st) => {
  if (!st.body_html && st.builder_config) {
    st.body_html = generateEmailHtml(st.builder_config);
  }
});

// Seed templates for initial view
export const INITIAL_MOCK_TEMPLATES = STARTER_TEMPLATES.map((st, idx) => ({
  id: `tmpl_${st.email_type.toLowerCase()}`,
  _id: `tmpl_${st.email_type.toLowerCase()}`,
  name: st.name,
  email_type: st.email_type,
  subject: st.subject,
  body_html: st.body_html || generateEmailHtml(st.builder_config),
  body_text: st.body_text,
  variables: st.variables,
  builder_config: st.builder_config,
  is_default_backup: idx < 4,
  createdAt: "2026-10-15T09:30:00Z",
  updatedAt: "2026-10-19T08:15:00Z",
}));

// Helper: Replace merge variables inside a template string
export const renderWithVariables = (text, variableMap = {}) => {
  if (!text) return "";
  let rendered = String(text);
  Object.keys(variableMap).forEach((key) => {
    const val = variableMap[key] ?? "";
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
    rendered = rendered.replace(regex, val);
  });
  return rendered;
};

// Unified Email Template API Service
export const EmailTemplateService = {
  // 1. List Templates
  list: async (params = {}) => {
    try {
      const res = await ApiClient.get(MODULE_CONFIG.endpoints.list, params);
      if (res?.success) return res;
    } catch (err) {
      console.warn("Failed to fetch email templates from API:", err);
    }
    return {
      success: true,
      data: INITIAL_MOCK_TEMPLATES,
      totalCount: INITIAL_MOCK_TEMPLATES.length,
      isLocalFallback: true,
    };
  },

  // 2. Create Template
  create: async (payload) => {
    try {
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.create, payload);
      if (res?.success) return res;
      return res;
    } catch (err) {
      console.error("Create template error:", err);
      return { success: false, message: "Failed to create email template." };
    }
  },

  // 3. Detail
  detail: async (id) => {
    try {
      const res = await ApiClient.get(`${MODULE_CONFIG.endpoints.detail}?id=${id}`);
      if (res?.success && (res?.data || res?.template)) return res;
    } catch (err) {
      console.warn("Fetch template detail from API error:", err);
    }
    const found = INITIAL_MOCK_TEMPLATES.find((t) => t.id === id || t._id === id);
    if (found) {
      return { success: true, data: found, isLocalFallback: true };
    }
    return { success: false, message: "Template not found." };
  },

  // 4. Update
  update: async (payload) => {
    try {
      const res = await ApiClient.put(MODULE_CONFIG.endpoints.update, payload);
      if (res?.success) return res;
      return res;
    } catch (err) {
      console.error("Update template error:", err);
      return { success: false, message: "Failed to update email template." };
    }
  },

  // 5. Server-Side Preview
  preview: async (payload) => {
    try {
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.preview, payload);
      if (res?.success) return res;
    } catch (err) {
      console.warn("Preview call error:", err);
    }
    return null;
  },

  // 6. Delete
  delete: async (id) => {
    try {
      const res = await ApiClient.delete(`${MODULE_CONFIG.endpoints.delete}?id=${id}`);
      return res;
    } catch (err) {
      console.error("Delete template error:", err);
      return { success: false, message: "Failed to delete email template." };
    }
  },
};
